import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Stack, useTheme } from '@mui/material';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import { NoAvailableMessageIcons } from 'app/shared/components/common/NoAvailableMessage/NoAvailableMessage.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { PROGRAM_FILTERS_GROUP_ID, SearchFilterConfigurationStatus } from 'constants/programs';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSearch from 'hooks/useSearch';
import useSort from 'hooks/useSort';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { getIsBestPricingSearch } from 'utils/programs';

import { resetFilterValue } from 'store/filters/actions';
import { getFiltersGroupValuesSelector } from 'store/filters/selectors';
import { showMessageAction } from 'store/global/actions';
import { resetLoanProgramOptionsDetailsAction } from 'store/loanApplications/actions';
import {
  selectIsLockedFromPricingStatus,
  selectIsOBEngineType,
  selectIsProgramChangeAllowed,
  selectLoanProgramOptionsGroupsWithDetails,
  selectLoanProgramsFilterDefs,
  selectOBFilterOptions,
  selectProgramChangeReasons,
} from 'store/loanApplications/selectors';
import { getAndCreateOnErrorPricingConfigurationAction } from 'store/system/actions';

import { columnsDefs, defaultSortOrder } from '../../LoanInfoModal.constant';
import BorderedFlexBox from '../BorderedFlexBox';

import ProgramAccordion from './components/ProgramAccordion';
import useAccordionsObserver from './components/ProgramAccordion/hooks/useAccordionsObserver';
import ProgramsFilterPanel from './components/ProgramsFilterPanel';
import ProgramsSkeletonLoader from './components/ProgramsSkeletonLoader/ProgramsSkeletonLoader';
import ProgramsTableHead from './components/ProgramsTableHead';
import ProgramsTableToolbar from './components/ProgramsTableToolbar';
import {
  hiddenFilterPanelStyles,
  programsAndPricingStyles,
  programsContentWrapperStyles,
  programsTableInnerStyles,
  programsTableScrollWrapperStyles,
  programsWrapperStyles,
  tableHeadRowStyles,
} from './ProgramsAndPricing.styles';
import {
  formatPendingChangesForHighlighting,
  getBestPricingNoticeMessage,
  getEmptyStateMessage,
  getFilteredLoanProgramOptionGroups,
  getIsBestPricingNoticeVisible,
  getIsFilterPanelHidden,
  getIsSearchFilterPreloaded,
  getShouldAutoSearchPrograms,
} from './ProgramsAndPricing.utils';

const ProgramsAndPricing = ({
  additionalProps,
  atParPayloadRef,
  canSelectProgram,
  isLoading,
  setIsFormChanged = _.noop,
  isFromPropertyAndLoan,
  isProgramReselection = false,
  searchFilterConfigurationStatus = SearchFilterConfigurationStatus.LOADING,
  setTempRateSheetId,
  rateSheetId,
  pendingChanges,
  getLoanOptions,
}) => {
  const dispatch = useDispatch();
  const { searchText, setSearchText } = useSearch();
  const { order, onRequestSort } = useSort(defaultSortOrder);

  const isLazyLoadPricingEnabled = useFeatureToggle(FeatureFlag.EnableLazyLoadPricing);
  const isExpiredPricingSaveEnabled = useFeatureToggle(FeatureFlag.EnableExpiredPricingSave);
  const isOptionsDeduplicateEnabled = useFeatureToggle(
    FeatureFlag.DeduplicateRemoteLoanOptionsOnGroups,
  );

  const [queryFilters, setQueryFilters] = useState(null);
  const [isHideNonEligible, setIsHideNonEligible] = useState(true);
  const [isShowExpiredPricing, setIsShowExpiredPricing] = useState(false);
  const [isIneligibleLoading, setIsIneligibleLoading] = useState(false);
  const [isLastSearchSucceeded, setIsLastSearchSucceeded] = useState(false);
  const autoSearchTriggeredRef = useRef(false);
  const ineligibleLoadedRef = useRef(false);

  const filterDefs = useSelector(selectLoanProgramsFilterDefs);
  const isOBEngineType = useSelector(selectIsOBEngineType);
  const isLocked = useSelector(selectIsLockedFromPricingStatus);
  const isProgramChangeAllowed = useSelector(selectIsProgramChangeAllowed);
  const programChangeReasons = useSelector(selectProgramChangeReasons);
  const filterOptions = useSelector(selectOBFilterOptions);
  const searchFilter = useSelector(
    getFiltersGroupValuesSelector({ filtersGroupId: PROGRAM_FILTERS_GROUP_ID }),
  );
  const loanProgramsOptionsGroupsWithDetails = useSelector(
    selectLoanProgramOptionsGroupsWithDetails,
  );

  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);

  const isLazyLoadEnabled = isLazyLoadPricingEnabled && isOBEngineType;

  const scrollWrapperSx = useMemo(
    () => [programsTableScrollWrapperStyles, scrollClasses.scroll],
    [scrollClasses.scroll],
  );

  const { selectedLoanProgramOption, setSelectedLoanProgramOption } = additionalProps ?? {};

  const formattedPendingChanges = useMemo(
    () => formatPendingChangesForHighlighting(pendingChanges),
    [pendingChanges],
  );

  const orderedLoanProgramsOptionsGroups = useMemo(
    () =>
      _.orderBy(
        getFilteredLoanProgramOptionGroups({
          searchText,
          loanProgramsOptionsGroupsWithDetails,
          isOBEngineType,
          searchFilter,
          isHideNonEligible,
          isShowExpiredPricing,
          isExpiredPricingSaveEnabled,
        }),
        [
          ({ programDetails }) => !programDetails?.eligibility?.isAllowed,
          ({ loanProgramOptionsGroup }) => {
            const value = loanProgramOptionsGroup[0]?.[order.id];

            return _.isString(value) ? _.toLower(value) : value;
          },
        ],
        ['asc', order.direction],
      ),
    [
      order,
      isOBEngineType,
      isHideNonEligible,
      isShowExpiredPricing,
      isExpiredPricingSaveEnabled,
      loanProgramsOptionsGroupsWithDetails,
      searchFilter,
      searchText,
    ],
  );

  const isBestPricingSearch = getIsBestPricingSearch({
    searchFilter: queryFilters,
    isLazyLoadEnabled,
    isOptionsDeduplicateEnabled,
  });

  const isBestPricingNoticeVisible = getIsBestPricingNoticeVisible({
    isBestPricingSearch,
    isLastSearchSucceeded,
    isLoading,
    loanProgramsOptionsGroupsWithDetails,
  });

  const isNoResult =
    !_.isNull(queryFilters) && !orderedLoanProgramsOptionsGroups.length && !isLoading;

  const isEmptyStateVisible =
    !orderedLoanProgramsOptionsGroups.length &&
    !isLoading &&
    !isIneligibleLoading &&
    !isBestPricingNoticeVisible;

  const isFilterPanelHidden = getIsFilterPanelHidden({
    isFromPropertyAndLoan,
    isOBEngineType,
    isLocked,
    searchFilterConfigurationStatus,
    filterOptions,
  });

  const emptyStateMessage = useMemo(
    () =>
      getEmptyStateMessage({
        isOBEngineType,
        isProgramChangeAllowed,
        programChangeReasons,
        queryFilters,
      }),
    [isOBEngineType, isProgramChangeAllowed, programChangeReasons, queryFilters],
  );

  useEffectSafeStrictMode(() => {
    dispatch(getAndCreateOnErrorPricingConfigurationAction());

    return () => {
      dispatch(resetLoanProgramOptionsDetailsAction());
      dispatch(resetFilterValue({ filtersGroupId: PROGRAM_FILTERS_GROUP_ID }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isNoResult) {
      setIsHideNonEligible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanProgramsOptionsGroupsWithDetails, queryFilters, isLoading]);

  const { registerAccordion, getAccordionObserverProps } = useAccordionsObserver(
    orderedLoanProgramsOptionsGroups.length,
  );

  const onSelectLoanProgramOption = useCallback(
    loanProgramOption => {
      if (canSelectProgram && !canSelectProgram(loanProgramOption)) {
        dispatch(
          showMessageAction({
            message: 'Change of Program is not Currently Allowed',
            variant: 'warning',
          }),
        );

        return;
      }

      setSelectedLoanProgramOption(loanProgramOption);
      setIsFormChanged(!!loanProgramOption);
    },
    [dispatch, setIsFormChanged, setSelectedLoanProgramOption, canSelectProgram],
  );

  const headerRowRenderer = useCallback(
    () => (
      <ProgramsTableHead
        columnsDefs={columnsDefs}
        customRowStyles={tableHeadRowStyles}
        order={order}
        onRequestSort={onRequestSort}
      />
    ),
    [onRequestSort, order],
  );

  const rowRenderer = useCallback(
    ({ key, loanProgramOptionsGroup, programDetails }) => (
      <ProgramAccordion
        atParPayloadRef={atParPayloadRef}
        columnsDefs={columnsDefs}
        id={key}
        key={key}
        loanProgramOptions={loanProgramOptionsGroup}
        observerProps={getAccordionObserverProps(key)}
        pendingChanges={formattedPendingChanges}
        programDetails={programDetails}
        registerAccordion={registerAccordion}
        selectedLoanProgramOption={selectedLoanProgramOption}
        onSelectLoanProgramOption={onSelectLoanProgramOption}
      />
    ),
    [
      atParPayloadRef,
      onSelectLoanProgramOption,
      selectedLoanProgramOption,
      formattedPendingChanges,
      registerAccordion,
      getAccordionObserverProps,
    ],
  );

  const onSearch = useCallback(async () => {
    ineligibleLoadedRef.current = false;
    await setIsHideNonEligible(true);
    const isSearchSucceeded = await getLoanOptions({ searchFilter });
    setIsLastSearchSucceeded(!!isSearchSucceeded);
    await setQueryFilters(searchFilter);
  }, [getLoanOptions, searchFilter]);

  useEffect(() => {
    if (
      isHideNonEligible ||
      !isLazyLoadEnabled ||
      ineligibleLoadedRef.current ||
      _.isNil(atParPayloadRef?.current)
    ) {
      return;
    }

    ineligibleLoadedRef.current = true;
    setIsIneligibleLoading(true);
    getLoanOptions({ searchFilter, isIneligibleRefetch: true })?.finally(() =>
      setIsIneligibleLoading(false),
    );
  }, [isHideNonEligible, isLazyLoadEnabled, atParPayloadRef, getLoanOptions, searchFilter]);

  const isSearchFilterPreloaded = useMemo(
    () => getIsSearchFilterPreloaded({ filterOptions, searchFilter }),
    [filterOptions, searchFilter],
  );

  useEffect(() => {
    const shouldAutoSearch = getShouldAutoSearchPrograms({
      isOBEngineType,
      isProgramReselection,
      isSearchFilterPreloaded,
    });

    if (!shouldAutoSearch || autoSearchTriggeredRef.current) {
      return;
    }

    autoSearchTriggeredRef.current = true;
    onSearch();
  }, [isOBEngineType, isProgramReselection, isSearchFilterPreloaded, onSearch]);

  return (
    <Stack sx={programsAndPricingStyles}>
      <BorderedFlexBox customStyles={programsContentWrapperStyles}>
        <ProgramsTableToolbar
          isFromPropertyAndLoan={isFromPropertyAndLoan}
          isHideNonEligible={isHideNonEligible}
          isLoading={isLoading}
          isShowExpiredPricing={isShowExpiredPricing}
          pendingChanges={formattedPendingChanges}
          rateSheetId={rateSheetId}
          setIsHideNonEligible={setIsHideNonEligible}
          setIsShowExpiredPricing={setIsShowExpiredPricing}
          setTempRateSheetId={setTempRateSheetId}
          onSearch={setSearchText}
        >
          <Box sx={isFilterPanelHidden ? hiddenFilterPanelStyles : undefined}>
            <ProgramsFilterPanel
              filterDefs={filterDefs}
              isDesiredTargetEnabled={isLazyLoadEnabled}
              isLoading={isLoading}
              isProgramReselection={isProgramReselection}
              searchFilterConfigurationStatus={searchFilterConfigurationStatus}
              onSearch={onSearch}
            />
          </Box>
        </ProgramsTableToolbar>

        <Box sx={scrollWrapperSx}>
          <Box sx={programsTableInnerStyles}>
            {headerRowRenderer()}

            <Stack sx={programsWrapperStyles}>
              {isBestPricingNoticeVisible && (
                <NoAvailableMessage
                  Icon={NoAvailableMessageIcons.NO_RESULTS}
                  message={getBestPricingNoticeMessage(isFilterPanelHidden)}
                />
              )}

              {isLoading && <ProgramsSkeletonLoader rowsAmount={3} />}

              {!isLoading && orderedLoanProgramsOptionsGroups.map(rowRenderer)}

              {isIneligibleLoading && <ProgramsSkeletonLoader rowsAmount={2} />}

              {isEmptyStateVisible && (
                <NoAvailableMessage
                  Icon={NoAvailableMessageIcons.NO_RESULTS}
                  message={emptyStateMessage}
                />
              )}
            </Stack>
          </Box>
        </Box>
      </BorderedFlexBox>
    </Stack>
  );
};

ProgramsAndPricing.propTypes = {
  ...WithLoadingPropTypes,
  additionalProps: PropTypes.object.isRequired,
  isFromPropertyAndLoan: PropTypes.bool.isRequired,
  getLoanOptions: PropTypes.func.isRequired,
  atParPayloadRef: PropTypes.shape({ current: PropTypes.object }),
  canSelectProgram: PropTypes.func,
  isProgramReselection: PropTypes.bool,
  searchFilterConfigurationStatus: PropTypes.oneOf(Object.values(SearchFilterConfigurationStatus)),
  setIsFormChanged: PropTypes.func,
  setTempRateSheetId: PropTypes.func,
};

export default memo(ProgramsAndPricing);
